import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';

interface Category {
    id: number;
    name: string;
}

const slugify = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export default function AdminPluginCreatePage() {
    const navigate = useNavigate();
    const { showNotification } = useNotificationDispatch();

    const [plugin, setPlugin] = useState({
        id: '',
        name: '',
        slug: '',
        tagline: '',
        description: '',
        price: 0.00,
        status: 'draft',
    });
    const [categoryIds, setCategoryIds] = useState<number[]>([]);
    const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        async function fetchCategories() {
            try {
                const response = await apiClient.get('/admin/categories');
                setAvailableCategories(response.data);
            } catch (error) {
                showNotification('Falha ao carregar categorias.', 'error');
            }
        }
        fetchCategories();
    }, [showNotification]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setPlugin(prev => {
            const newSlug = (name === 'name') ? slugify(value) : prev.slug;
            const newId = (name === 'name') ? slugify(value) : prev.id;
            return { ...prev, [name]: value, slug: newSlug, id: newId };
        });
    };

    const handleCategoryChange = (categoryId: number) => {
        setCategoryIds(prevIds => 
            prevIds.includes(categoryId) 
                ? prevIds.filter(id => id !== categoryId) 
                : [...prevIds, categoryId]
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        const payload = { ...plugin, categoryIds, price: Number(plugin.price) };

        try {
            const response = await apiClient.post('/admin/plugins', payload);
            showNotification('Plugin criado com sucesso! Redirecionando para a página de edição...', 'success');
            // Redireciona para a página de edição do plugin recém-criado
            setTimeout(() => navigate(`/admin/plugins/edit/${response.data.pluginId}`), 1500);
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao criar o plugin.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-text-primary">Adicionar Novo Plugin</h1>
                <Link to="/admin/plugins" className="px-4 py-2 text-sm font-semibold text-text-secondary bg-white border rounded-lg hover:bg-gray-50">
                    &larr; Voltar
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-subtle space-y-6 max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="name" className="form-label">Nome do Plugin</label>
                        <input type="text" name="name" value={plugin.name} onChange={handleChange} required className="form-input" placeholder="Ex: SuperClans" />
                    </div>
                    <div>
                        <label htmlFor="slug" className="form-label">ID / Slug (será usado na URL)</label>
                        <input type="text" name="slug" value={plugin.slug} onChange={handleChange} required className="form-input bg-gray-100" readOnly placeholder="Ex: super-clans" />
                    </div>
                </div>

                <div>
                    <label htmlFor="tagline" className="form-label">Tagline (Slogan)</label>
                    <input type="text" name="tagline" value={plugin.tagline} onChange={handleChange} className="form-input" placeholder="Um breve slogan para o plugin" />
                </div>
                <div>
                    <label htmlFor="description" className="form-label">Descrição</label>
                    <textarea name="description" value={plugin.description} onChange={handleChange} rows={4} className="form-input" placeholder="Descreva o que o plugin faz."></textarea>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div>
                        <label htmlFor="price" className="form-label">Preço (R$)</label>
                        <input type="number" name="price" value={plugin.price} onChange={handleChange} step="0.01" className="form-input" />
                    </div>
                    <div>
                        <label htmlFor="status" className="form-label">Status Inicial</label>
                        <select name="status" value={plugin.status} onChange={handleChange} className="form-input">
                            <option value="draft">Rascunho</option>
                            <option value="published">Publicado</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="form-label">Categorias</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-4 border rounded-lg bg-gray-50/50">
                        {availableCategories.map((category) => (
                            <label key={category.id} className="flex items-center space-x-2 cursor-pointer">
                                <input type="checkbox" checked={categoryIds.includes(category.id)} onChange={() => handleCategoryChange(category.id)} className="form-checkbox" />
                                <span>{category.name}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end gap-4 pt-4 border-t">
                    <Link to="/admin/plugins" className="px-5 py-2.5 text-sm font-semibold text-text-secondary bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</Link>
                    <button type="submit" disabled={saving} className="px-6 py-2.5 text-sm font-semibold text-white bg-brand-blue rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
                        {saving ? 'Criando Plugin...' : 'Criar e Continuar para Edição'}
                    </button>
                </div>
            </form>
        </div>
    );
}